/* © 2024-2026 Johannes Roob. Proprietärer Code. Keine Nutzung oder Weitergabe ohne Erlaubnis. | Proprietary code. No use or distribution without permission. */

import { mustMap } from "./must.js";
import { Form } from "../controls/custom/Form.js";
import { Window } from "../controls/custom/Window.js";
import { WindowManager } from "../controls/custom/WindowManager.js";

export class FormManager {
    #forms: Map<HTMLElement, Form> = new Map();
    #formsById: Map<string, Form> = new Map();     // Key: Form.id; Value: Form
    formHost: HTMLElement | undefined;
    windowManager = new WindowManager();

    create(element: HTMLElement) {
        if (!element.matches("div.Form")) {
            throw new Error("this is not a Form");
        }

        if (this.#forms.has(element)) {
            const f = mustMap(this.#forms, element);
            throw new Error(`Form by ID ${f.id} already exists`);
        }

        // Special form: Window
        const windowElement = element.querySelector<HTMLElement>("div.Window");

        if (windowElement !== null) {
            const w = new Window(this, element, windowElement);
            this.windowManager.add(w);
            this.#forms.set(element, w);
            this.#formsById.set(w.id, w);
            w.init();
            return w;
        }

        // Add other special form here.
        // ...

        // Form
        const f = new Form(this, element);
        this.#forms.set(element, f);
        this.#formsById.set(f.id, f);

        if (f.element.parentElement === this.formHost) {
            if (f.callingFormId === "") {
                // Place Form inside FormHost of active Window.
                const w = this.windowManager.getActiveWindow();

                if (w !== undefined) {
                    w.formHost.append(f.element);
                }
            } else {
                const callingForm = this.#formsById.get(f.callingFormId);

                if (callingForm instanceof Window) {
                    // Place Form inside the FormHost of the specified Window.
                    const w = this.windowManager.mustWindowById(callingForm.id);
                    w.formHost.appendChild(f.element);
                }
            }
        }

        f.init();
        return f;
    }

    destroy(element: HTMLElement) {
        if (!element.matches("div.Form")) {
            throw new Error("this is not a Form");
        }

        const f = mustMap(this.#forms, element);
        f.destroy();
        this.#forms.delete(element);
        this.#formsById.delete(f.id);

        if (f instanceof Window) {
            this.windowManager.delete(f);
        }
    }

    mustById(formId: string) {
        return mustMap(this.#formsById, formId);
    }

    getById(formId: string) {
        return this.#formsById.get(formId);
    }

    destroyAll() {
        for (const f of this.#forms.values()) {
            f.destroy();
        }

        this.#forms.clear();
        this.#formsById.clear();
        this.windowManager.destroyAll();
    }
}